import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorCode } from '@eombor/contracts';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { authApi } from './auth.store.js';

/**
 * CAPTCHA holati: rasmni oladi, kodni TEKSHIRADI va chiptani saqlaydi.
 *
 * Sahifada BITTA nusxa bo'ladi va uni ikkala kirish yo'li ham ishlatadi
 * (login-parol va Telegram). Ikkita alohida rasm chizilsa, odam qaysi
 * biriga yozishni o'ylab qolardi.
 *
 * Tekshiruv kod TO'LIQ terilgan zahoti, o'zi ketadi — tugma bosish shart
 * emas. Tasdiqlangach ekranda maydon o'rniga bitta qator qoladi va odam
 * faqat parol bilan ovora bo'ladi.
 */

/** Serverdagi kod uzunligi (`captcha.ts` dagi `LENGTH`) bilan bir xil. */
const CODE_LENGTH = 5;

/**
 * Rasm muddati tugashiga shuncha soniya qolganda O'ZI yangilanadi.
 *
 * Bu butun nosozlikning tuzatilishi: ilgari mijoz muddatni bilmasdi va rasm
 * ekranda abadiy turardi, javob esa serverda 2 daqiqada o'chib ketardi.
 * Kirish sahifasini ochib qo'yib parolini olgani ketgan odam qaytganda
 * EKRANDA TURGAN kodni to'g'ri terardi va "kod noto'g'ri" javobini olardi.
 *
 * Zaxira tarmoq kechikishini qoplaydi: kod muddat tugashiga 10 soniya qolganda
 * terilsa ham, so'rov serverga ulgurishi kerak.
 */
const REFRESH_MARGIN_SECONDS = 10;

/** Server juda qisqa muddat aytsa ham, rasm hech bo'lmasa shuncha turadi. */
const MIN_REFRESH_SECONDS = 5;

/**
 * Tasdiq shu yerda SAQLANADI — bir soat davomida (serverdagi muddat bilan
 * bir xil).
 *
 * `localStorage`, `sessionStorage` emas: odam brauzerni yopib qayta ochsa
 * ham kod qaytadan so'ralmasligi kerak — butun maqsad shu.
 *
 * Bu SIR emas: tasdiq serverda manzilga bog'langan, ya'ni nusxalab boshqa
 * mashinada ishlatib bo'lmaydi. Shuning uchun uni oddiy xotirada saqlash
 * xavfsiz.
 */
const PASS_KEY = 'eombor:captcha-pass';

/**
 * Tekshiruv xatosini ODAMGA aytiladigan xabarga o'giradi.
 *
 * "Kod noto'g'ri terildi" — bu AYBLOV va uni faqat server aynan shunday
 * deganda aytish mumkin. Ilgari bu yerda ikki tarmoq bor edi: `expired`
 * bo'lsa "muddati tugadi", QOLGAN HAMMA HOLDA "kod noto'g'ri". O'sha
 * "qolgan hamma" ichiga server o'chiqligi (`status 0`), serverning
 * yiqilishi (5xx), so'rov chegarasi (429) va kutilmagan javob ham kirardi.
 * Oqibati: backend ko'tarilmagan mashinada odam ekrandagi kodni to'g'ri
 * terib ham HAR SAFAR "kod noto'g'ri terildi" javobini olardi — aybni
 * o'zidan qidirardi, holbuki server umuman javob bermayotgan edi.
 *
 * `CAPTCHA_EXPIRED` uchun ajratilgan xabar `errors.ts` da izohlangan sabab
 * bilan shu yerda qoladi.
 */
const errorKeyFor = (cause: unknown): string => {
  if (!(cause instanceof ApiClientError)) return 'login.captchaUnavailable';
  if (cause.code === ErrorCode.CAPTCHA_EXPIRED) return 'login.captchaExpired';
  // 400 — serverning O'Z hukmi: kod haqiqatan noto'g'ri terilgan.
  // Boshqa hamma narsa (0, 429, 5xx, sxemaga tushmagan javob) odamning
  // aybi emas va unga shunday deyish yolg'on bo'lardi.
  return cause.status === 400 ? 'login.captchaWrong' : 'login.captchaUnavailable';
};

interface StoredPass {
  token: string;
  expiresAt: string;
}

const readPass = (): string | null => {
  try {
    const raw = localStorage.getItem(PASS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPass>;
    if (typeof parsed.token !== 'string' || typeof parsed.expiresAt !== 'string') return null;
    // Bir daqiqa zaxira: muddati tugash arafasidagi tasdiq bilan kirishga
    // urinib, "muddati tugadi" xatosiga uchramaslik uchun.
    if (new Date(parsed.expiresAt).getTime() - 60_000 <= Date.now()) return null;
    return parsed.token;
  } catch {
    return null;
  }
};

const writePass = (pass: StoredPass): void => {
  try {
    localStorage.setItem(PASS_KEY, JSON.stringify(pass));
  } catch {
    // Saqlanmadi — kod har safar so'raladi, lekin kirish ishlayveradi.
  }
};

const clearPass = (): void => {
  try {
    localStorage.removeItem(PASS_KEY);
  } catch {
    // Yuqoridagi bilan bir xil sabab.
  }
};

export interface CaptchaState {
  svg: string | null;
  loading: boolean;
  answer: string;
  setAnswer: (value: string) => void;
  /** Tekshiruv ketayotgan payt — maydon qulflanadi. */
  checking: boolean;
  /** Tasdiqlangan chipta. `null` — hali tasdiqlanmagan. */
  token: string | null;
  error: string | null;
  /** Yangi rasm oladi va tasdiqni bekor qiladi. */
  reload: () => void;
  /**
   * Saqlangan tasdiqni BEKOR qiladi va kodni qaytadan so'raydi.
   *
   * `reload` dan farqi: bu saqlangan tasdiqni ham o'chiradi. Server
   * tasdiqni rad etgan paytda (muddati tugagan, manzil o'zgargan)
   * chaqiriladi — oddiy "parol noto'g'ri" xatosida esa YO'Q, aks holda
   * odam har xato paroldan keyin yana kod terishga majbur bo'lardi.
   */
  invalidate: () => void;
}

export const useCaptcha = (): CaptchaState => {
  const [id, setId] = useState<string | null>(null);
  const [svg, setSvg] = useState<string | null>(null);
  /** Serverdan kelgan yashash muddati — yangilash taymeri shundan hisoblanadi. */
  const [expiresIn, setExpiresIn] = useState<number | null>(null);
  const [answer, setAnswerState] = useState('');
  const [checking, setChecking] = useState(false);
  // Saqlangan tasdiq bo'lsa rasm UMUMAN so'ralmaydi — ekranda ham
  // ko'rinmaydi va bu butun maqsad.
  const [token, setToken] = useState<string | null>(() => readPass());
  const [loading, setLoading] = useState(() => readPass() === null);
  const [error, setError] = useState<string | null>(null);

  // Yopilgan komponent holatini yangilashga urinmaslik uchun.
  const alive = useRef(true);
  useEffect(() => {
    /*
     * Bayroq har o'rnatishda QAYTA yoqiladi.
     *
     * `useEffect(() => () => { alive.current = false }, [])` yozilgan edi va
     * u ishlamadi: React dasturchi rejimida effektni ikki marta chaqiradi
     * (o'rnatish → tozalash → o'rnatish), tozalash esa bayroqni butunlay
     * o'chirib qo'yardi. Natijada javob kelgani bilan holat yangilanmasdi va
     * rasm mangu "yuklanmoqda" holatida qolardi.
     */
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setAnswerState('');
    setToken(null);
    setError(null);
    /*
     * Eski `id` DARHOL unutiladi — javob kelguncha emas.
     *
     * Aks holda yangi rasm yuklanayotgan paytda maydon eski, allaqachon
     * kuydirilgan `id` ni ushlab turardi va shu oraliqda terilgan kod
     * har doim "noto'g'ri" javobini olardi.
     */
    setId(null);
    setExpiresIn(null);
    void authApi
      .captcha()
      .then((challenge) => {
        if (!alive.current) return;
        setId(challenge.id);
        setSvg(challenge.svg);
        setExpiresIn(challenge.expiresIn);
      })
      .catch(() => {
        // Rasm kelmasa maydon bo'sh qoladi va "yangi rasm" tugmasi ko'rinadi:
        // kirishni butunlay to'sib qo'yish o'rniga qayta urinish taklif
        // qilinadi.
        if (alive.current) setSvg(null);
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  }, []);

  // Tasdiq allaqachon bo'lsa rasm so'ralmaydi: keraksiz so'rov ham,
  // ekranda bir zumga chaqnab o'tadigan maydon ham bo'lmaydi.
  useEffect(() => {
    if (readPass() === null) reload();
  }, [reload]);

  /**
   * Rasm muddati tugashidan OLDIN o'zi yangilanadi.
   *
   * Shu tufayli ekranda ko'rinib turgan kod HAR DOIM server hali eslayotgan
   * kod bo'ladi — sahifa qancha ochiq turishidan qat'i nazar.
   *
   * Tasdiqlangach (`token`) yangilanmaydi: rasm ishini bajarib bo'lgan.
   * Tekshiruv ketayotganda ham tegilmaydi — javob baribir yo chipta beradi,
   * yo o'zi yangi rasm chaqiradi.
   */
  useEffect(() => {
    if (id === null || expiresIn === null || token !== null || checking) return;
    const seconds = Math.max(expiresIn - REFRESH_MARGIN_SECONDS, MIN_REFRESH_SECONDS);
    const timer = setTimeout(reload, seconds * 1000);
    return () => clearTimeout(timer);
  }, [id, expiresIn, token, checking, reload]);

  /**
   * Kod to'liq terilgan zahoti tekshiriladi.
   *
   * Xato bo'lsa YANGI rasm olinadi: server javobni bir marta qabul qiladi
   * (to'g'ri bo'lsa ham, noto'g'ri bo'lsa ham o'chiradi), ya'ni eski rasm
   * baribir yaroqsiz. Yangilanmasa, ikkinchi urinish har doim xato bilan
   * tugardi va odam sababini tushunmasdi.
   */
  const verify = useCallback(
    (code: string) => {
      const captchaId = id;
      if (captchaId === null) return;

      setChecking(true);
      setError(null);
      void authApi
        .captchaVerify(captchaId, code)
        .then((ticket) => {
          writePass({ token: ticket.captchaToken, expiresAt: ticket.expiresAt });
          if (alive.current) setToken(ticket.captchaToken);
        })
        .catch((cause: unknown) => {
          if (!alive.current) return;
          /*
           * Tartib MUHIM: `reload()` xato xabarini tozalaydi (u yangi rasm
           * uchun toza boshlanish tayyorlaydi), shuning uchun xabar undan
           * KEYIN qo'yiladi.
           *
           * Teskari yozilgan edi va oqibati shunday bo'lgan: kod noto'g'ri
           * terilsa, ekranda jimgina yangi rasm paydo bo'lardi — odam
           * nima uchun ekanini bilmasdi va yana o'sha xatoni takrorlardi.
           */
          reload();
          /*
           * Xabar SABABGA qarab tanlanadi — `errorKeyFor` ga qarang. Odamni
           * ayblaydigan matn faqat server uni ayblaganda chiqadi.
           */
          setError(t(errorKeyFor(cause)));
        })
        .finally(() => {
          if (alive.current) setChecking(false);
        });
    },
    [id, reload],
  );

  const setAnswer = useCallback(
    (value: string) => {
      const code = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, CODE_LENGTH);
      setAnswerState(code);
      setError(null);
      if (code.length === CODE_LENGTH) verify(code);
    },
    [verify],
  );

  const invalidate = useCallback(() => {
    clearPass();
    reload();
  }, [reload]);

  return { svg, loading, answer, setAnswer, checking, token, error, reload, invalidate };
};
